import Enumerable from 'linq'
import { Item, ItemJewelery, ItemJeweleryStone } from '../type'
import { PrintArgs, PrintJewelryVariables } from '../../../core/component/Printer/printer.hook'
import { getStoneCode } from './jewelery-stone-code'
import { getMountingCodedPrice } from './price-code'

export type JewelryPrintJob = PrintArgs<PrintJewelryVariables>

/**
 * Builds the set of jewelry tag print jobs for an item.
 *
 * A physical tag holds at most two stone lines. Products with more stones than
 * fit on one tag produce additional tags — every non-stone field is repeated on
 * every tag, and only the two stone lines (secondText / thirdText) differ.
 *
 * Prioritisation is unchanged from the original single-tag logic: Diamond /
 * Lab-Diamond stones (rich format) come first, other stones (short format)
 * follow. The first job therefore reproduces the original single tag exactly.
 * An item with no stones still yields exactly one tag (original behavior).
 */
export const buildJewelryPrintJobs = (entity: Item): JewelryPrintJob[] => {
  const jewelery = entity.jewelery ?? {} as ItemJewelery

  let fourthText: string

  const categoryTitles = [
    entity.category?.title,
    entity.subcategory?.title,
    entity.subcategory2?.title,
  ]
    .filter(Boolean)
    .map(t => t?.toLowerCase())

  if (
    Enumerable.from(['ring', 'wedding', 'engagement']).any(text => categoryTitles.includes(text))
  ) {
    fourthText = [
      jewelery.material,
      jewelery.width,
      jewelery.fingerSize ?? '',
    ]
      .join('-')
  } else {
    fourthText = [
      jewelery.material,
      jewelery.dwt,
      jewelery.width,
      jewelery.length,
      jewelery.fingerSize ?? '',
    ]
      .join('-')
  }

  let stoneText: string[] = []

  if (jewelery.stones?.length) {
    const stones = jewelery.stones ?? []

    const isDiamondFamily = (st: ItemJeweleryStone) =>
      ['diamond', 'lab diamond'].includes((st.name ?? '').toLowerCase())

    const formattedWeight = (st: ItemJeweleryStone) =>
      st.totalWeight
        ? parseFloat(st.totalWeight.toString()).toFixed(2)
        : undefined

    // Rich format: {code}-{quantity}-{total carat weight}-{color}-{clarity}
    // Missing color/clarity are dropped so the output degrades gracefully
    // (e.g. D-5-0.50) instead of producing trailing dashes.
    const richFormat = (st: ItemJeweleryStone) =>
      [
        getStoneCode(st.name),
        st.quantity,
        formattedWeight(st),
        st.color,
        st.clarity,
      ]
        .filter(value => value !== undefined && value !== null && value !== '')
        .join('-')

    // Short format: {code}-{quantity}-{total carat weight}
    const shortFormat = (st: ItemJeweleryStone) =>
      [
        getStoneCode(st.name),
        st.quantity,
        formattedWeight(st),
      ].join('-')

    // Every Diamond / Lab-Diamond stone gets the rich format, not just the
    // first one. Diamond-family stones are prioritised ahead of other stones
    // so that, when more entries exist than print slots, diamonds win.
    const diamondStones = stones.filter(isDiamondFamily)
    const otherStones = stones.filter(st => !isDiamondFamily(st))

    stoneText = [
      ...diamondStones.map(richFormat),
      ...otherStones.map(shortFormat),
    ]
  }

  const isMounting = entity.subcategory?.title?.trim().toLowerCase() === 'mounting'

  // Mounting tags print the cost price encoded with the company pricing key and
  // without the `K` prefix, so the amount cannot be read off the tag. The cost
  // is rounded up to the next $10 for the tag only (e.g. 229.50 -> 230 -> `LBX`);
  // the stored cost price is left untouched.
  const priceText = isMounting
    ? getMountingCodedPrice(entity.costPrice ?? 0)
    : `K${entity.listPrice}`

  // Every tag repeats all non-stone product information; only the stone lines
  // (secondText / thirdText) differ between tags.
  const base: Omit<PrintJewelryVariables, 'secondText' | 'thirdText'> = {
    firstText: entity.style,
    fourthText,
    fifthText: entity.sku,
    sixthText: [
      entity.vendor?.contactId,
      priceText,
    ].join('-'),
    barcode: entity.sku,
  }

  // A tag holds at most two stones. Chunk the prioritised stones into pairs so
  // every stone is printed; the first pair reproduces today's single tag
  // exactly. With no stones we still emit one tag (today's behavior).
  const pairs: string[][] = stoneText.length
    ? Array.from(
      { length: Math.ceil(stoneText.length / 2) },
      (_, i) => stoneText.slice(i * 2, i * 2 + 2),
    )
    : [[]]

  return pairs.map(([secondText, thirdText]) => ({
    template_name: 'jewelry' as const,
    variables: {
      ...base,
      secondText: secondText ?? '',
      thirdText: thirdText ?? '',
    },
  }))
}
